import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { z } from 'zod'
import DashboardLayout from '../components/DashboardLayout'
import SelectOrgs from '@components/SelectOrgs'
import SsoConnectionFormFields from '@components/SsoConnectionFormFields'
import {
    attributeMapFromFields,
    buildSsoTestLoginUrl,
    CONNECTION_ID_ERROR,
    CONNECTION_ID_REGEX,
    defaultSsoFieldsValues,
    HL_ACCOUNTS_UNCONFIGURED_MESSAGE,
    normalizeUsernameAttribute,
    parseEmailDomains,
    payloadForMode,
    resolveHlAccountsBaseUrl,
    ssoFieldsObjectSchema,
    ssoModeRefine,
    SsoFieldsValues,
} from '@components/SsoConnectionFormFields.helpers'
import useSession from '@hooks/useSession'
import { HASsoResult, SsoConnection } from '@types'

// Phase 2 — Step 6.
//
// Guided "onboard a new SSO tenant" flow. Functionally close to
// CreateSsoConnectionPage but with three deliberate differences:
//
//   1. Defaults flip `enabled` to `true` — this flow is
//      "I'm onboarding a tenant right now" rather than
//      CreateSsoConnectionPage's "I'm staging a row to enable
//      later." `enforce_sso` and `allow_idp_initiated` still default
//      to false so existing password users keep working and the
//      stricter IdP-initiated path stays opt-in.
//   2. After successful create, we stay on this page and surface a
//      success card with a "Test login" link
//      (`<HL_ACCOUNTS>/sso/saml/<slug>/login?redirect=...`) so
//      staff can immediately exercise the round-trip end-to-end
//      before flipping enforce_sso. Replaces Create's redirect-and-
//      toast pattern.
//   3. Framing copy is onboarding-specific — explains the flow,
//      points to the Phase 0 DNS / SP key items as prerequisites
//      for prod, etc.
//
// Reuses the shared SsoConnectionFormFields component (and its
// schema + payloadForMode helpers) so any future field additions —
// or the metadata-ingestion cron landing — flow into here for free.

const SUBMIT_URL = '/api/ha/v2/sso-connections'

const pageSchema = z.object({
    org_id: z.string().min(1, 'Pick an organisation.'),
    connection_id: z
        .string()
        .min(1, 'Connection ID is required.')
        .max(64, 'Connection ID is too long (max 64 chars).')
        .regex(CONNECTION_ID_REGEX, CONNECTION_ID_ERROR),
})

const formSchema = pageSchema
    .merge(ssoFieldsObjectSchema)
    .superRefine(ssoModeRefine)
type FormValues = z.infer<typeof formSchema>

// "Safer-by-default for an onboarding context": enable the
// connection so the staff can test it immediately, but keep
// enforce_sso off so existing password users aren't locked out
// before round-trip verification, and keep IdP-initiated off
// (it's the riskier path — stricter audience binding etc.).
const seedDefaults = (orgId: string | undefined): FormValues => ({
    ...defaultSsoFieldsValues(),
    org_id: orgId ?? '',
    connection_id: '',
    enabled: true,
    enforce_sso: false,
    allow_idp_initiated: false,
})

export default function OnboardSsoTenantPage() {
    const session = useSession()!
    const [, setLocation] = useLocation()

    const initialOrgId = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('org') ?? undefined
    }, [])

    const [form, setForm] = useState<FormValues>(() =>
        seedDefaults(initialOrgId)
    )
    const [errors, setErrors] = useState<
        Partial<Record<keyof FormValues, string>>
    >({})
    const [submitting, setSubmitting] = useState(false)
    const [topError, setTopError] = useState<string | null>(null)

    // Domain-ownership confirmation gate. The 2026-05-01 re-audit
    // flagged that we have no operator-side check that the domains
    // being claimed actually belong to the tenant — uniqueness is
    // enforced at the DB level, but authority to claim isn't. An
    // ops mistake (typing `microsoft.com` for what should have been
    // `umn.edu`) silently breaks SSO discovery for the legitimate
    // owner. The checkbox below makes the operator pause and confirm.
    // Server-side audit-trail entry on every domain claim/release
    // captures the moment for forensics (see habitat-admin's
    // sso-connection.model.ts).
    const [domainsConfirmed, setDomainsConfirmed] = useState(false)

    // After a successful create we don't navigate away — we render
    // a success card on this same page with a test-login link.
    // Holds the created row so the card can show its slug + build
    // the test URL.
    const [created, setCreated] = useState<SsoConnection | null>(null)

    const isDirty = useMemo(() => {
        const baseline = seedDefaults(initialOrgId)
        return Object.keys(form).some(
            (key) =>
                form[key as keyof FormValues] !==
                baseline[key as keyof FormValues]
        )
    }, [form, initialOrgId])

    // Single memoised parse feeds both the submit gate and the
    // per-field error effect — the schema carries a superRefine plus a
    // per-domain refine, so we don't want to run it twice per render
    // (matches EditSsoConnectionPage's `parsedForm` pattern).
    const parsedForm = useMemo(() => formSchema.safeParse(form), [form])
    const formValid = parsedForm.success

    // The operator's syntactically-valid claimed domains. Drives
    // whether the ownership-confirmation checkbox is rendered.
    // Connections without domains skip the gate entirely — they're
    // staging rows that won't be discoverable via the login form, so
    // there's no authority-to-claim concern.
    const parsedDomains = useMemo(
        () => parseEmailDomains(form.email_domains_raw ?? ''),
        [form.email_domains_raw]
    )
    const hasDomains = parsedDomains.length > 0
    const submitDisabled =
        !formValid || submitting || (hasDomains && !domainsConfirmed)

    useEffect(() => {
        if (parsedForm.success) {
            setErrors({})
            return
        }
        const next: Partial<Record<keyof FormValues, string>> = {}
        for (const issue of parsedForm.error.issues) {
            const field = issue.path[0] as keyof FormValues
            if (!next[field]) next[field] = issue.message
        }
        setErrors(next)
    }, [parsedForm])

    // Re-confirm ownership whenever the claimed domain set changes, so
    // the tick always corresponds to what's actually being submitted.
    const domainsKey = parsedDomains.join(',')
    useEffect(() => {
        setDomainsConfirmed(false)
    }, [domainsKey])

    const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }))

    const setSharedField = <K extends keyof SsoFieldsValues>(
        key: K,
        value: SsoFieldsValues[K]
    ) => set(key as keyof FormValues, value as FormValues[keyof FormValues])

    const cancelToList = () => {
        if (
            isDirty &&
            !window.confirm(
                'Discard this form? Anything you typed will be lost.'
            )
        ) {
            return
        }
        setLocation(
            form.org_id
                ? `/manage-sso-connections?org=${encodeURIComponent(form.org_id)}`
                : '/manage-sso-connections'
        )
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        // Enforce the domain-ownership gate here too, not just via the Submit
        // button's `disabled` prop — the submission path owns the gate, so it
        // can't be bypassed by any other submit trigger.
        if (!formValid || submitting || (hasDomains && !domainsConfirmed))
            return

        setSubmitting(true)
        setTopError(null)

        const emailDomains = parseEmailDomains(form.email_domains_raw ?? '')
        const modeFields = payloadForMode(form)

        const payload = {
            org_id: form.org_id,
            connection_id: form.connection_id,
            provider_type: 'saml' as const,
            idp_entity_id: form.idp_entity_id,
            idp_sso_url: form.idp_sso_url,
            idp_signing_cert: modeFields.idp_signing_cert,
            idp_metadata_url: modeFields.idp_metadata_url,
            idp_metadata_xml: modeFields.idp_metadata_xml,
            nameid_format: form.nameid_format,
            attribute_map: attributeMapFromFields(form),
            username_attribute: normalizeUsernameAttribute(
                form.username_attribute
            ),
            allow_idp_initiated: form.allow_idp_initiated,
            enforce_sso: form.enforce_sso,
            enabled: form.enabled,
            require_response_signature: form.require_response_signature,
            email_domains: emailDomains,
        }

        try {
            const response = await fetch(SUBMIT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.accessToken}`,
                },
                body: JSON.stringify(payload),
            })
            const body = (await response.json()) as HASsoResult<SsoConnection>
            if (!response.ok || body.error) {
                setTopError(
                    body.error
                        ? body.message
                        : `Failed to create connection (HTTP ${response.status})`
                )
                setSubmitting(false)
                return
            }
            setCreated(body.result)
            setSubmitting(false)
        } catch (err) {
            console.error(err)
            setTopError(
                err instanceof Error
                    ? err.message
                    : 'Failed to create connection'
            )
            setSubmitting(false)
        }
    }

    if (created) {
        // Guard the test-login link: in a build with no
        // VITE_HL_ACCOUNTS_BASE_URL configured `buildSsoTestLoginUrl`
        // throws, so resolve the base URL first and only offer the
        // button when it's available.
        const hlAccountsBaseUrl = resolveHlAccountsBaseUrl()
        const testLoginUrl = hlAccountsBaseUrl
            ? buildSsoTestLoginUrl(created.connection_id)
            : null

        return (
            <DashboardLayout>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: 4,
                        paddingBottom: 8,
                    }}
                >
                    <Paper sx={{ p: 4, width: '100%', maxWidth: 720 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ mb: 1 }}
                        >
                            <CheckCircleIcon color="success" />
                            <Typography variant="h6">
                                Tenant onboarded
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{ color: 'gray', mb: 3 }}
                        >
                            The connection is created and{' '}
                            <strong>
                                {created.enabled === 1 ? 'enabled' : 'disabled'}
                            </strong>
                            . Run a real SAML round-trip below before flipping{' '}
                            <code>enforce_sso</code> on — that's the gate that
                            disables password / magic-link login for users in
                            this org.
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                            rowGap={1}
                            sx={{ mb: 3 }}
                        >
                            <Chip
                                label={`Connection ID: ${created.connection_id}`}
                                variant="outlined"
                            />
                            <Chip
                                label={`Provider: ${created.provider_type.toUpperCase()}`}
                                variant="outlined"
                            />
                            <Chip
                                color={
                                    created.enabled === 1
                                        ? 'success'
                                        : 'default'
                                }
                                label={`Enabled: ${created.enabled === 1 ? 'on' : 'off'}`}
                            />
                            <Chip
                                color={
                                    created.enforce_sso === 1
                                        ? 'warning'
                                        : 'default'
                                }
                                label={`Enforce SSO: ${created.enforce_sso === 1 ? 'on' : 'off'}`}
                            />
                        </Stack>

                        {testLoginUrl ? (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <strong>Test the round-trip now.</strong>{' '}
                                Clicking the button below opens the IdP login
                                flow in a new tab — sign in with a real test
                                account for this tenant, then watch the redirect
                                land back on <code>/sso/callback</code>. If
                                anything 4xx&apos;s you, fix the connection in
                                the management page before flipping{' '}
                                <code>enforce_sso</code>.
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                {HL_ACCOUNTS_UNCONFIGURED_MESSAGE} The
                                connection was still created — set the env var
                                for this environment to enable the in-app
                                round-trip test.
                            </Alert>
                        )}

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<OpenInNewIcon />}
                                disabled={!testLoginUrl}
                                onClick={() =>
                                    testLoginUrl &&
                                    window.open(
                                        testLoginUrl,
                                        '_blank',
                                        'noopener,noreferrer'
                                    )
                                }
                            >
                                Test login (opens new tab)
                            </LoadingButton>
                            <LoadingButton
                                variant="outlined"
                                size="large"
                                startIcon={<FormatListBulletedIcon />}
                                onClick={() =>
                                    setLocation(
                                        `/manage-sso-connections?org=${encodeURIComponent(
                                            created.org_id
                                        )}&created=${encodeURIComponent(
                                            created.connection_id
                                        )}`
                                    )
                                }
                            >
                                Back to connections list
                            </LoadingButton>
                        </Stack>

                        {testLoginUrl && (
                            <>
                                <Divider sx={{ my: 3 }} />

                                <Typography
                                    variant="caption"
                                    sx={{ color: 'gray', display: 'block' }}
                                >
                                    Test login URL: <code>{testLoginUrl}</code>
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Box>
            </DashboardLayout>
        )
    }

    return (
        <DashboardLayout>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 4,
                    paddingBottom: 8,
                }}
            >
                <Paper sx={{ p: 4, width: '100%', maxWidth: 720 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ mb: 1 }}
                    >
                        <RocketLaunchIcon color="primary" />
                        <Typography variant="h6">Onboard SSO tenant</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'gray', mb: 1 }}>
                        Guided flow for landing a new tenant&apos;s SAML SSO
                        configuration into Habitat. Walks you through pick- org
                        → name-slug → IdP-config → email-domains, then gives you
                        a <strong>test login link</strong> to verify the
                        round-trip works end-to-end before flipping{' '}
                        <code>enforce_sso</code> on.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'gray', mb: 3 }}>
                        Defaults are tuned for onboarding:{' '}
                        <strong>enabled = on</strong>, but{' '}
                        <code>enforce_sso</code> and{' '}
                        <code>allow_idp_initiated</code> stay{' '}
                        <strong>off</strong> so existing password users keep
                        working and the riskier IdP-initiated path stays opt-in.
                    </Typography>
                    <Alert
                        severity="info"
                        icon={<FormatListBulletedIcon />}
                        sx={{ mb: 3 }}
                    >
                        <strong>First time?</strong> The full step-by-step
                        runbook lives at <code>docs/onboard-tenant.md</code> in
                        this repo. Covers what to collect from the tenant&apos;s
                        IT team (entityID, SSO URL, signing cert, email domains,
                        attribute map), the smoke-test sequence, the go-live
                        levers, and the rollback / troubleshooting matrix.
                    </Alert>

                    {topError && (
                        <Alert
                            severity="error"
                            onClose={() => setTopError(null)}
                            sx={{ mb: 3 }}
                        >
                            {topError}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            <Box>
                                <SelectOrgs
                                    selectedOrg={form.org_id || undefined}
                                    setSelectedOrg={(value) =>
                                        set(
                                            'org_id',
                                            (typeof value === 'function'
                                                ? value(form.org_id)
                                                : value) ?? ''
                                        )
                                    }
                                    required
                                />
                                {errors.org_id && (
                                    <Typography variant="caption" color="error">
                                        {errors.org_id}
                                    </Typography>
                                )}
                            </Box>

                            <TextField
                                label="Connection ID (slug)"
                                helperText={
                                    errors.connection_id ??
                                    'Lowercase alphanumeric + hyphens. Becomes part of the public login URL: /sso/saml/<slug>/login'
                                }
                                error={!!errors.connection_id}
                                value={form.connection_id}
                                onChange={(e) =>
                                    set('connection_id', e.target.value)
                                }
                                required
                                fullWidth
                            />

                            <SsoConnectionFormFields
                                values={form}
                                setValue={setSharedField}
                                errors={errors}
                            />

                            {hasDomains && (
                                <Alert
                                    severity={
                                        domainsConfirmed ? 'success' : 'warning'
                                    }
                                    sx={{ mt: 2 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={domainsConfirmed}
                                                onChange={(e) =>
                                                    setDomainsConfirmed(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2">
                                                    I confirm that this tenant
                                                    owns the email domain(s)
                                                    entered above.
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Login-form discovery will
                                                    route any user with an email
                                                    at these domains to this
                                                    tenant's IdP. Claiming a
                                                    domain a tenant doesn't own
                                                    silently breaks SSO for the
                                                    legitimate owner. See{' '}
                                                    <code>
                                                        docs/onboard-tenant.md
                                                    </code>{' '}
                                                    §1.4.
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                </Alert>
                            )}

                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                spacing={2}
                            >
                                <LoadingButton
                                    onClick={cancelToList}
                                    color="inherit"
                                >
                                    Cancel
                                </LoadingButton>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={submitting}
                                    disabled={submitDisabled}
                                    startIcon={<RocketLaunchIcon />}
                                >
                                    Onboard tenant
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    </form>
                </Paper>
            </Box>
        </DashboardLayout>
    )
}
